import type { DateTime } from "luxon";
import { useCallback } from "react";
import { FEATURE_FLAGS } from "../../../contexts/FeatureFlagsProvider";
import { useHospitalData } from "../../../contexts/HospitalDataProvider";
import { useWeekIndex } from "../../../contexts/WeekIndexProvider/hook";
import { useFeatureFlag } from "../../../hooks/useOpenFeatureFlag";
import { getVisibleWeekOffset, resolveDayCell, visibleWeekIndexForDate } from "../../../utils/dateUtils";

export type GapsVisibleWeek = {
	/** True when weeks start on the user's chosen day rather than Sunday — the desktop reads `visibleWeekViewData` then. */
	isDynamicWeekStartEnabled: boolean;
	/** The visible week a calendar date falls in, or `-1` when no loaded visible week holds it. */
	weekIndexForDate: (date: DateTime) => number;
	/** The day in `column` of visible week `weekIndex`, or the nearest earlier column that is loaded; `undefined` if none is. */
	dayInWeek: (weekIndex: number, column: number) => DateTime | undefined;
};

/**
 * The weeks the gaps-first home pages through, drawn exactly as the desktop weekly view draws them. With
 * `StaffingDynamicWeekStart` on, a week starts on the user's `viewStartDay` and spills into the next data
 * week (`WeekViewProvider.visibleWeekViewData`); off, it is the Sunday data week. The shared `weekIndex`
 * means the same thing on both screens, so the phone and the desktop show the same seven days for it.
 */
export const useGapsVisibleWeek = (): GapsVisibleWeek => {
	const { hospitalData, getWeekIndexByDate } = useHospitalData();
	const { viewStartDay } = useWeekIndex();
	const isDynamicWeekStartEnabled = useFeatureFlag(FEATURE_FLAGS.StaffingDynamicWeekStart);
	const offset = isDynamicWeekStartEnabled ? getVisibleWeekOffset(viewStartDay) : 0;

	// By CALENDAR date: the URL day is midnight, and the shift-day lookup would roll it back before day start.
	const weekIndexForDate = useCallback(
		(date: DateTime): number => visibleWeekIndexForDate(getWeekIndexByDate(date), date, offset),
		[getWeekIndexByDate, offset],
	);

	const dayInWeek = useCallback(
		(weekIndex: number, column: number): DateTime | undefined => {
			// The last visible week can run past the loaded schedule when it starts after Sunday, so a column with
			// no loaded day falls back to the nearest earlier one that has it.
			for (let candidate = column; candidate >= 0; candidate--) {
				const { sourceWeekIndex, sourceDayIndex } = resolveDayCell(weekIndex, candidate, offset);
				const day = hospitalData.weeks[sourceWeekIndex]?.weeklyShifts[0]?.days[sourceDayIndex];
				if (day) return day.date;
			}
			return undefined;
		},
		[hospitalData.weeks, offset],
	);

	return { isDynamicWeekStartEnabled, weekIndexForDate, dayInWeek };
};
